import { countryNums, pwdPublicKey, TencentCaptchaAppId } from '@/config/api'
import { getPrivacyVersion, isEmailRegistered, register } from '@/src/api/api'
import Image from '@/src/components/CustomImage'
import useI18n from '@/src/hooks/use_i18n'
import { loadScript, pwdEncode } from '@/src/utils/tools'
import Tippy from '@tippyjs/react'
import { useUpdate } from 'ahooks'
import classNames from 'classnames'

import { Eye, EyeClosed, LoaderCircle } from 'lucide-react'
import React, { useMemo, useRef, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import { toast } from 'react-toastify'
import 'tippy.js/dist/tippy.css'
import CheckPrivacyModal from './CheckPrivacyModal'
import SuccessModal from './SuccessModal'
import styles from './index.module.scss'
interface RegisterFormProps {
  country: string
}

interface ErrorState {
  valid: boolean
  msg: string
}

type CallBack = (state: ErrorState) => void

const initForm = () => {
  return {
    phone: '',
    email: '',
    company_name: '',
    company_url: '',
    mark: '',
    agree_privacy: false,
    password: ''
  }
}

const RegisterForm: React.FC<RegisterFormProps> = ({ country }) => {
  const { $t, i18nFormatAll, activeLocale: lang } = useI18n()
  const message = useMemo(() => $t('ResConsolePIndex.global'), [$t])
  const isLoadingTCaptcha = useRef(false)
  const countryCode = useMemo(() => countryNums[country?.toLowerCase()], [country])
  const forceUpdate = useUpdate()
  const [showAgreePrivacyModal, setShowAgreePrivacyModal] = useState(false)
  const formValue = useRef(initForm())

  const formState = useRef({
    phone: {
      valid: false,
      msg: ''
    },
    email: {
      valid: false,
      msg: ''
    },
    company_name: {
      valid: false,
      msg: ''
    },
    company_url: {
      valid: false,
      msg: ''
    },
    mark: {
      valid: false,
      msg: ''
    },
    password: {
      valid: false,
      msg: ''
    }
  })

  const showCreditCard = useRef(country?.toLowerCase() !== 'cn')
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [showPassword, setShowPassword] = useState(false)
  const connectionRules = {
    email: async (value: any, evenType, callback?: CallBack) => {
      const trimmedValue = value.trim()
      const state = { valid: false, msg: '' }
      const reg = /^[_a-zA-Z0-9-]+(\.[_a-zA-Z0-9-]+)*@[a-zA-Z0-9-]+([\\.][a-zA-Z0-9-]+)+$/
      // 检查邮箱格式
      if (trimmedValue.length < 5 || trimmedValue.length > 250) {
        state.valid = true
        state.msg = message.__email250characters
      } else if (!reg.test(trimmedValue) && trimmedValue.length > 4) {
        state.valid = true
        state.msg = message.__emailAddress
      } else if (lang !== 'zh-cn') {
        // 个人邮箱域名列表
        const personalEmailDomains = [
          '@gmail.com',
          '@outlook.com',
          '@hotmail.com',
          '@yahoo.com',
          '@yahoo.co.jp',
          '@icloud.com'
        ]
        // 检查是否是个人邮箱
        const isPersonalEmail = personalEmailDomains.some((domain) => value.toLowerCase().endsWith(domain))
        if (isPersonalEmail) {
          state.valid = true
          state.msg = message.__businessEmailTips
        }
      } else if (evenType === 'onBlur') {
        // 检查邮箱是否已注册
        const res = await isEmailRegistered({ email: value, language: lang })
        if (res.code !== 0) {
          state.valid = true
          state.msg = $t(`ResGlobalErrorCodeAll.global.${res.code}`)
        }
      }

      formState.current.email = { ...state }
      if (callback) {
        callback(state)
        return
      }
      forceUpdate()
    },
    company_name: (value: any, _, callback?: CallBack) => {
      const trimmedValue = value.trim()
      const state = { valid: false, msg: '' }
      if (trimmedValue.length < 3 || trimmedValue.length > 250) {
        state.valid = true
        state.msg = message.__companyName250characters
      }
      formState.current.company_name = { ...state }

      if (callback) {
        callback(state)
        return
      }
      forceUpdate()
    },
    company_url: (value: any, _, callback?: CallBack) => {
      const trimmedValue = value.trim()
      const state = { valid: false, msg: '' }
      // setCompanyLinkExists(false)
      // const urlReg = /[a-zA-Z0-9][-a-zA-Z0-9]{0,62}(\.[a-zA-Z0-9][-a-zA-Z0-9]{0,62})+\.?/
      if (trimmedValue && (trimmedValue.length < 10 || trimmedValue.length > 250)) {
        state.valid = true
        state.msg = message.__companyWebsite250characters
      }
      // else if (!value.startsWith('http')) {
      //   state.valid = true
      //   state.msg = message.__companyWebsiteAddress
      // }
      formState.current.company_url = { ...state }
      if (callback) {
        callback(state)
        return
      }
      forceUpdate()
    },
    phone: (value: any, _, callback?: CallBack) => {
      const state = { valid: false, msg: '' }
      const valid = country?.toLowerCase() === 'cn' ? /^1\d{10}$/.test(value) : /^\d{5,15}$/.test(value)
      if (!valid) {
        const msg = country?.toLowerCase() === 'cn' ? message.__phoneNoError : message.__phoneNo15numbers
        state.valid = true
        state.msg = msg
      }
      formState.current.phone = { ...state }
      if (callback) {
        callback(state)
        return
      }
      forceUpdate()
    },
    password: (value: any, _, callback?: CallBack) => {
      const state = { valid: false, msg: '' }
      // 密码格式校验规则
      const hasNumber = /\d/.test(value)
      const hasLetter = /[a-zA-Z]/.test(value)
      const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/.test(value)
      const isLengthValid = value.length >= 8 && value.length <= 16
      const isFormatValid = hasNumber && hasLetter && hasSpecial

      // 判断不同的错误情况
      if (!value) {
        state.valid = true
        state.msg = message.__password_error1
      } else if (!isLengthValid && !isFormatValid) {
        state.valid = true
        state.msg = message.__password_error1
      } else if (!isLengthValid) {
        state.valid = true
        state.msg = message.__password_error2
      } else if (!isFormatValid) {
        state.valid = true
        state.msg = message.__password_error3
      }

      formState.current.password = { ...state }
      if (callback) {
        callback(state)
        return
      }
      forceUpdate()
    }
  }

  const validateForm = () => {
    // 遍历每个字段的规则并执行验证
    const result: ErrorState[] = []
    Object.entries(connectionRules).forEach(([field, rules]) => {
      // 执行每个规则的验证
      rules(formValue.current[field], 'onChange', (state) => {
        result.push(state)
      })
    })
    // 检查所有字段是否都有效
    return result.every((state) => !state.valid)
  }

  // 校验同意隐私协议
  const checkAgreePrivacy = async () => {
    if (!formValue.current.agree_privacy) {
      setShowAgreePrivacyModal(true)
      return Promise.reject()
    }
    return Promise.resolve()
  }
  // 提交前校验
  const validateSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault()
    const isValid = validateForm()
    if (!isValid) {
      return forceUpdate()
    }
    await checkAgreePrivacy()
    showTencentCaptcha()
  }

  // 显示腾讯滑块验证
  const showTencentCaptcha = async () => {
    try {
      if (isSubmitting) return
      setIsSubmitting(true)
      // 加载验证码脚本
      if (!window.TencentCaptcha) {
        await loadScript('https://turing.captcha.qcloud.com/TCaptcha.js')
      }
      // 初始化验证码
      const captcha = new window.TencentCaptcha(TencentCaptchaAppId, handleSubmit, {
        userLanguage: lang,
        ready: () => {
          // 使用 MutationObserver 监听 DOM 变化
          // setIsCaptchaShowing(true)
          const captchaWrapper = document.getElementById('tcaptcha_transform_dy')
          if (captchaWrapper) {
            captchaWrapper.style.pointerEvents = 'auto'
          }
        }
      })

      captcha.show()
    } catch (error) {
      console.error('加载验证码失败:', error)
      const ticket = 'trerror_1001_' + TencentCaptchaAppId + Math.floor(new Date().getTime() / 1000)
      handleSubmit({
        ret: 0,
        randstr: '@' + Math.random().toString(36).substr(2),
        ticket: ticket,
        errorCode: 1001,
        errorMessage: 'jsload_error'
      })
    } finally {
      // setIsSubmitting(false)
    }
  }
  // 提交表单
  const handleSubmit = async (res: any) => {
    if (res.ret === 0) {
      try {
        const [versionRes, encodePwd] = await Promise.all([
          getPrivacyVersion(),
          pwdEncode(formValue.current.password, pwdPublicKey)
        ])
        // console.log('versionRes', versionRes)
        const data = await register({
          ...formValue.current,
          mark: formValue.current.mark.trim(),
          password: encodePwd,
          randstr: res.randstr,
          ticket: res.ticket,
          phone_country_code: countryCode,
          language: lang,
          privacy_agreement_version: versionRes?.data?.version || '1.0'
        })
        if (data.code !== 0) {
          toast.warn($t(`ResGlobalErrorCodeAll.global.${data.code}`) || $t(`ResGlobalErrorCodeAll.global.-1`))
          return
        }
        window.ga?.sendEvent?.('api-账号注册', '注册成功')
        setShowSuccessModal(true)
        formValue.current = initForm()
      } catch (error) {
        console.log('error', error)
      } finally {
        setIsSubmitting(false)
      }
    } else {
      setIsSubmitting(false)
      // console.error('验证码验证失败:', res)
    }
  }
  const handleFormItemValidate = (field: string, evenType: 'onBlur' | 'onChange') => {
    if (connectionRules[field]) {
      connectionRules[field](formValue.current[field], evenType)
    } else {
      forceUpdate()
    }
  }
  const handleFormItemChange = (field: string, value: any) => {
    // 如果是字符串类型的值，去除前后空格
    formValue.current[field] = value
    handleFormItemValidate(field, 'onChange')

    // 提前加载腾讯滑块验证码js, 避免用户体验不佳
    if (!isLoadingTCaptcha.current && !window.TencentCaptcha) {
      isLoadingTCaptcha.current = true
      loadScript('https://turing.captcha.qcloud.com/TCaptcha.js')
    }
  }

  const handleAgree = () => {
    formValue.current.agree_privacy = true
    handleClose()
    // 提交表单
    showTencentCaptcha()
  }
  const handleClose = () => {
    setShowAgreePrivacyModal(false)
    setShowSuccessModal(false)
  }

  return (
    <div className={styles.formContainer}>
      <div className={styles.headTitle}>
        <h3>{message.__registerTitleTip}</h3>
        {showCreditCard && (
          <div className={styles.creditCard}>
            <Image src="/assets/images/api/credit-card.svg" width={20} height={20} alt="credit card icon" />
            <span>{message.__creditCardTip}</span>
          </div>
        )}
      </div>

      <Form className={styles.formContent} validated={false}>
        <div className={styles.formColumns}>
          <Form.Group>
            <Form.Label aria-required htmlFor="company_name">
              {message.__companyName}
              <span className="text-danger">*</span>
            </Form.Label>
            <Tippy content={message.__companyTip} placement="left">
              <Form.Control
                id="company_name"
                value={formValue.current.company_name}
                onChange={(e) => handleFormItemChange('company_name', e.target.value)}
                onBlur={() => handleFormItemValidate('company_name', 'onBlur')}
                isInvalid={formState.current.company_name.valid}
                placeholder=""
                maxLength={250}
              />
            </Tippy>

            <Form.Control.Feedback type="invalid">{formState.current.company_name.msg}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label htmlFor="company_url">{message.__companyWebsite}</Form.Label>
            <Form.Control
              id="company_url"
              value={formValue.current.company_url}
              onChange={(e) => {
                if (!/[\u4e00-\u9fa5]/.test(e.target.value)) {
                  handleFormItemChange('company_url', e.target.value)
                }
              }}
              onBlur={() => handleFormItemValidate('company_url', 'onBlur')}
              isInvalid={formState.current.company_url.valid}
              placeholder=""
              maxLength={250}
            />
            <Form.Control.Feedback type="invalid">{formState.current.company_url.msg}</Form.Control.Feedback>
          </Form.Group>
        </div>
        <div className={styles.formColumns}>
          <Form.Group>
            <Form.Label aria-required htmlFor="phone">
              {message.__phoneNo}
              <span className="text-danger">*</span>
            </Form.Label>
            <Tippy content={message.__phoneTip} placement="left">
              <InputGroup className={classNames({ 'is-invalid': formState.current.phone.valid })}>
                <InputGroup.Text id="phone code">{countryCode}</InputGroup.Text>
                <Form.Control
                  id="phone"
                  value={formValue.current.phone}
                  onChange={(e) => handleFormItemChange('phone', e.target.value)}
                  onBlur={() => handleFormItemValidate('phone', 'onBlur')}
                  isInvalid={formState.current.phone.valid}
                  placeholder=""
                  maxLength={15}
                />
              </InputGroup>
            </Tippy>

            <Form.Control.Feedback type="invalid">{formState.current.phone.msg}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label aria-required htmlFor="email">
              {message.__workEmail}
              <span className="text-danger">*</span>
            </Form.Label>
            <Tippy content={message.__emailTip} placement="left">
              <Form.Control
                // type="email"
                id="email"
                value={formValue.current.email}
                onChange={(e) => handleFormItemChange('email', e.target.value)}
                onBlur={() => handleFormItemValidate('email', 'onBlur')}
                isInvalid={formState.current.email.valid}
                placeholder=""
                maxLength={250}
              />
            </Tippy>

            <Form.Control.Feedback type="invalid">{formState.current.email.msg}</Form.Control.Feedback>
          </Form.Group>
        </div>
        <Form.Group>
          <Form.Label aria-required htmlFor="password">
            {message.__password}
            <span className="text-danger">*</span>
          </Form.Label>
          <Tippy content={message.__password_error1} placement="left">
            <InputGroup className={classNames(styles.password, { 'is-invalid': formState.current.password.valid })}>
              <Form.Control
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formValue.current.password}
                onChange={(e) => handleFormItemChange('password', e.target.value)}
                onBlur={() => handleFormItemValidate('password', 'onBlur')}
                isInvalid={formState.current.password.valid}
                placeholder=""
                // maxLength={16}
              />

              <Button
                // variant="outline-secondary"
                onClick={() => setShowPassword(!showPassword)}
                className={styles.passwordToggle}
              >
                {!showPassword ? <EyeClosed /> : <Eye />}
              </Button>
            </InputGroup>
          </Tippy>

          <Form.Control.Feedback type="invalid">{formState.current.password.msg}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group>
          <Form.Label htmlFor="mark">{message.__tellUsHowYouWant}</Form.Label>
          <Form.Control
            id="mark"
            as="textarea"
            value={formValue.current.mark}
            onChange={(e) => handleFormItemChange('mark', e.target.value)}
            onBlur={() => handleFormItemValidate('mark', 'onBlur')}
            isInvalid={formState.current.mark.valid}
            placeholder=""
            maxLength={1000}
          />
          <Form.Control.Feedback type="invalid">{formState.current.mark.msg}</Form.Control.Feedback>
        </Form.Group>
        <Form.Check
          type="checkbox"
          checked={formValue.current.agree_privacy}
          onChange={(e) => handleFormItemChange('agree_privacy', e.target.checked)}
          label={
            <p
              dangerouslySetInnerHTML={{
                __html: i18nFormatAll(message.__privacyDesc, {
                  0: `<a target="_blank" href="https://www.17track.net/helpcenter/licensing">${message.__privacy01}</a>`,
                  1: `<a target="_blank" href="https://www.17track.net/helpcenter/privacy">${message.__privacy02}</a>`
                })
              }}
            />
          }
        />
        {/* 提交按钮 */}
        <Button
          // type="submit"
          className={classNames(styles.submitButton, isSubmitting ? 'opacity-50 cursor-not-allowed bg-opacity-80' : '')}
          onClick={(e) => validateSubmit(e)}
          disabled={isSubmitting}
        >
          {isSubmitting && <LoaderCircle className="animate-spin" size={16} />}
          {message.__submitTip}
        </Button>
      </Form>
      <CheckPrivacyModal show={showAgreePrivacyModal} handleAgree={handleAgree} handleClose={handleClose} />
      <SuccessModal show={showSuccessModal} handleClose={handleClose} />
    </div>
  )
}

export default RegisterForm
